import {useField} from 'formik';
import {isBoolean} from 'lodash';
import {useCallback, useMemo} from 'react';
import {
    NumericFormat,
    NumberFormatValues,
    NumericFormatProps,
} from 'react-number-format';

import {InputProps} from '../Input';

import {InputAdapter} from './InputAdapter';

type Props = {
    name: string,
    label: string,
} & Partial<NumericFormatProps> & Partial<InputProps>;

export const NumericFieldFormik: React.FC<Props> = ({
    name,
    label,
    required,
    hasError: hasErrorProp,
    ...rest
}) => {
    const [field, meta, helpers] = useField(name);

    const hasError = useMemo<boolean>(
        () => {
            if (isBoolean(hasErrorProp)) {
                return hasErrorProp;
            }

            return meta.error && (meta.touched || meta.value) || meta.touched && required && !meta.value;
        },
        [meta, hasErrorProp, required]
    );

    const handleChange = useCallback(
        ({floatValue}: NumberFormatValues) => {
            helpers.setValue(floatValue === undefined ? null : floatValue);
        },
        [helpers],
    );

    const handleBlur = useCallback((e: React.FocusEvent) => {
        field.onBlur(e);
    }, [field]);

    return (
        <NumericFormat
            {...rest}
            label={label}
            name={name}
            value={field.value}
            customInput={InputAdapter}
            required={required}
            hasError={hasError}
            errorMessage={meta.error}
            handleBlur={handleBlur}
            onValueChange={handleChange}
        />
    );
};
